import { useState } from "react";
import { adminCreateManualBooking } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { extractErrorMessage } from "../lib/errors";
import { Plus, Trash2, ChevronDown } from "lucide-react";

const SLOT_TIMES = ["08:00", "10:45", "13:30", "16:00"];
const PRODUCTS = [
  { key: "standard", label: "Standard 2-hour lesson", slots: 1 },
  { key: "package10", label: "10-hour package", slots: 5 },
  { key: "package20", label: "20-hour package", slots: 10 },
  { key: "bespoke", label: "Bespoke", slots: 1 },
];

export default function ManualBookingForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [product, setProduct] = useState("standard");
  const [transmission, setTransmission] = useState("manual");
  const [slots, setSlots] = useState([{ date: "", time: "08:00" }]);
  const [customer, setCustomer] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    pickup_address: "", pickup_town: "", pickup_postcode: "",
  });
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const addSlot = () => setSlots([...slots, { date: "", time: "08:00" }]);
  const removeSlot = (i) => setSlots(slots.filter((_, j) => j !== i));
  const updateSlot = (i, key, value) => {
    setSlots(slots.map((s, j) => j === i ? { ...s, [key]: value } : s));
  };

  const reset = () => {
    setProduct("standard"); setTransmission("manual");
    setSlots([{ date: "", time: "08:00" }]);
    setCustomer({ customer_name: "", customer_email: "", customer_phone: "", pickup_address: "", pickup_town: "", pickup_postcode: "" });
    setPaymentMethod("cash"); setAmount(""); setNotes(""); setSendEmail(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!customer.customer_name || !customer.customer_email) {
      toast.error("Customer name and email are required.");
      return;
    }
    const cleanSlots = slots.filter(s => s.date && s.time);
    if (cleanSlots.length === 0) {
      toast.error("Add at least one date + time slot.");
      return;
    }
    try {
      setSubmitting(true);
      await adminCreateManualBooking({
        product, transmission, slots: cleanSlots,
        ...customer,
        payment_method: paymentMethod,
        amount: parseFloat(amount) || 0,
        notes,
        send_email: sendEmail,
      });
      toast.success("Manual booking created.");
      reset();
      onCreated && onCreated();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not create booking."));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="mt-8 bg-white border border-black/5 rounded-3xl overflow-hidden" data-testid="manual-booking-form">
      <button type="button" onClick={() => setOpen(!open)}
              className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-[#F9FAFB]">
        <div>
          <div className="text-base font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Add a manual booking</div>
          <p className="text-xs text-[#9CA3AF] mt-0.5">For lessons paid by cash, bank transfer, voucher, etc. — outside the website.</p>
        </div>
        <ChevronDown className={`h-5 w-5 text-[#4B5563] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <form onSubmit={submit} className="px-6 pb-6 space-y-5 border-t border-black/5 pt-5">
          {/* Product + transmission */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Product</Label>
              <select className="mt-1 w-full h-11 px-3 rounded-lg border border-black/10 bg-white text-sm"
                      value={product} onChange={e => setProduct(e.target.value)} data-testid="mb-product">
                {PRODUCTS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Transmission</Label>
              <select className="mt-1 w-full h-11 px-3 rounded-lg border border-black/10 bg-white text-sm"
                      value={transmission} onChange={e => setTransmission(e.target.value)} data-testid="mb-transmission">
                <option value="manual">Manual</option>
                <option value="auto">Automatic</option>
              </select>
            </div>
          </div>

          {/* Slots */}
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Slots</Label>
              <Button type="button" size="sm" variant="outline" className="border-black/15" onClick={addSlot} data-testid="mb-add-slot">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add slot
              </Button>
            </div>
            <div className="mt-2 space-y-2">
              {slots.map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input type="date" value={s.date} onChange={e => updateSlot(i, "date", e.target.value)} className="h-11 flex-1" data-testid={`mb-slot-${i}-date`} />
                  <select className="h-11 px-3 rounded-lg border border-black/10 bg-white text-sm w-28"
                          value={s.time} onChange={e => updateSlot(i, "time", e.target.value)} data-testid={`mb-slot-${i}-time`}>
                    {SLOT_TIMES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {slots.length > 1 && (
                    <button type="button" className="p-2 rounded hover:bg-red-50" onClick={() => removeSlot(i)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Customer details */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Customer name *</Label>
              <Input value={customer.customer_name} onChange={e => setCustomer({...customer, customer_name: e.target.value})} className="h-11 mt-1" required data-testid="mb-name" />
            </div>
            <div>
              <Label className="text-xs">Email *</Label>
              <Input type="email" value={customer.customer_email} onChange={e => setCustomer({...customer, customer_email: e.target.value})} className="h-11 mt-1" required data-testid="mb-email" />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input value={customer.customer_phone} onChange={e => setCustomer({...customer, customer_phone: e.target.value})} className="h-11 mt-1" data-testid="mb-phone" />
            </div>
            <div>
              <Label className="text-xs">Pickup address</Label>
              <Input value={customer.pickup_address} onChange={e => setCustomer({...customer, pickup_address: e.target.value})} className="h-11 mt-1" data-testid="mb-address" />
            </div>
            <div>
              <Label className="text-xs">Town</Label>
              <Input value={customer.pickup_town} onChange={e => setCustomer({...customer, pickup_town: e.target.value})} className="h-11 mt-1" data-testid="mb-town" />
            </div>
            <div>
              <Label className="text-xs">Postcode</Label>
              <Input value={customer.pickup_postcode} onChange={e => setCustomer({...customer, pickup_postcode: e.target.value.toUpperCase()})} className="h-11 mt-1" data-testid="mb-postcode" />
            </div>
          </div>

          {/* Payment */}
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">Payment method</Label>
              <select className="mt-1 w-full h-11 px-3 rounded-lg border border-black/10 bg-white text-sm"
                      value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} data-testid="mb-payment-method">
                <option value="cash">Cash</option>
                <option value="bank-transfer">Bank transfer</option>
                <option value="voucher">Voucher / gift card</option>
                <option value="card">Card (offline / SumUp / etc.)</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Amount paid (£)</Label>
              <Input type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} className="h-11 mt-1" data-testid="mb-amount" />
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} data-testid="mb-send-email" />
                Send confirmation email to customer
              </label>
            </div>
          </div>

          <div>
            <Label className="text-xs">Notes (internal)</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="mt-1" data-testid="mb-notes" />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" className="border-black/15" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-[#37B160] hover:bg-[#2D9450] text-white" data-testid="mb-submit">
              {submitting ? "Creating..." : "Create booking"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
